import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, Clock, FileText, AlertCircle, AlertTriangle, Users, Calendar, Sparkles, CalendarClock, MessageSquare, Pencil, Plus } from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';
import { WorkflowBuilder, LifecycleDashboard, ConversationalBuilder } from '../components/agentic';
import { WorkflowDefinitionDialog } from '../components/WorkflowDefinitionDialog';

// Compute a human-friendly SLA countdown ("2d 4h left" / "Overdue 3h 10m") + colour tone.
const computeSLA = (dueDate, nowMs) => {
  if (!dueDate) return null;
  const diff = new Date(dueDate).getTime() - nowMs;
  const overdue = diff < 0;
  const abs = Math.abs(diff);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const label = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  let tone = 'green';
  if (overdue) tone = 'red';
  else if (diff <= 4 * 3600000) tone = 'orange';
  else if (diff <= 2 * 86400000) tone = 'amber';
  return { overdue, text: overdue ? `Overdue ${label}` : `${label} left`, tone };
};

const SLA_TONE_CLASSES = {
  red: 'bg-red-100 text-red-700',
  orange: 'bg-orange-100 text-orange-700',
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-emerald-100 text-emerald-700',
};

const SLABadge = ({ dueDate, nowMs }) => {
  const sla = computeSLA(dueDate, nowMs);
  if (!sla) return null;
  return (
    <span
      data-testid="sla-countdown-badge"
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${SLA_TONE_CLASSES[sla.tone]}`}
    >
      <Clock className="w-3 h-3" />
      {sla.text}
    </span>
  );
};

const Workflows = () => {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = (user?.roles || []).some((r) => ['CompanyAdmin', 'SuperAdmin'].includes(r));
  const [myTasks, setMyTasks] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentDialog, setCommentDialog] = useState({ open: false, instanceId: null, action: null });
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [defDialog, setDefDialog] = useState({ open: false, workflow: null });
  const notifiedRef = useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  // Tick once a minute so the SLA countdown badges stay live.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // In-app notification: summarise overdue / due-soon pending tasks once after load.
  useEffect(() => {
    if (loading || notifiedRef.current) return;
    const now = Date.now();
    const overdue = myTasks.filter((t) => t.due_date && new Date(t.due_date).getTime() < now).length;
    const dueSoon = myTasks.filter((t) => {
      if (!t.due_date) return false;
      const diff = new Date(t.due_date).getTime() - now;
      return diff >= 0 && diff <= 24 * 3600000;
    }).length;
    if (overdue > 0 || dueSoon > 0) {
      const parts = [];
      if (overdue > 0) parts.push(`${overdue} overdue`);
      if (dueSoon > 0) parts.push(`${dueSoon} due within 24h`);
      toast.warning(`Pending workflow tasks: ${parts.join(', ')}`, {
        description: 'Review your My Tasks list to stay within SLA.',
      });
    }
    notifiedRef.current = true;
  }, [loading, myTasks]);

  const loadData = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [tasksResponse, workflowsResponse] = await Promise.all([
        api.getMyTasksDetailed(headers),
        api.listWorkflows(headers)
      ]);

      setMyTasks(tasksResponse.data);
      setWorkflows(workflowsResponse.data);
    } catch (error) {
      console.error('Error loading workflows:', error);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const openCommentDialog = (instanceId, action) => {
    setCommentDialog({ open: true, instanceId, action });
    setCommentText('');
  };

  const closeCommentDialog = () => {
    setCommentDialog({ open: false, instanceId: null, action: null });
    setCommentText('');
  };

  const submitCommentAction = async () => {
    const { instanceId, action } = commentDialog;
    // For approve, comment is optional; for reject/request_changes it's mandatory
    if (action !== 'approve' && !commentText.trim()) {
      toast.error('Please provide a comment explaining your decision');
      return;
    }
    try {
      setSubmitting(true);
      const headers = getAuthHeaders();
      await api.performWorkflowAction(instanceId, action, commentText.trim(), headers);
      const verb = action === 'reject' ? 'rejected' : action === 'approve' ? 'approved' : 'returned for changes';
      toast.success(`Workflow ${verb} successfully`);
      closeCommentDialog();
      loadData();
    } catch (error) {
      console.error('Error performing workflow action:', error);
      toast.error('Failed to perform action');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading workflows...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="workflows-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Workflows</h1>
        <p className="text-slate-600 mt-1">Manage document workflows and approvals</p>
      </div>

      <Tabs defaultValue="tasks" className="w-full">
        <TabsList className="grid grid-cols-5 max-w-3xl">
          <TabsTrigger value="tasks" data-testid="my-tasks-tab">My Tasks ({myTasks.length})</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="workflows-tab">Definitions</TabsTrigger>
          <TabsTrigger value="ai-builder" className="flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            AI Builder
          </TabsTrigger>
          <TabsTrigger value="ai-chat" className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            AI Chat
          </TabsTrigger>
          <TabsTrigger value="lifecycle" className="flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            Lifecycle
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-4">
          {myTasks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No pending tasks</h3>
                <p className="text-slate-500">You&apos;re all caught up!</p>
              </CardContent>
            </Card>
          ) : (
            myTasks.map((task) => {
              const overdue = isOverdue(task.due_date);
              const docTitle = task.document?.title || 'Unknown Document';
              const docType = task.document?.doc_type || '';
              const workflowName = task.workflow_name || 'Workflow';
              
              return (
              <Card key={task.id} data-testid={`task-${task.id}`} className={overdue ? 'border-red-300' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        {docTitle}
                        {task.escalated && (
                          <AlertTriangle className="w-5 h-5 text-red-600" title="Escalated" />
                        )}
                      </CardTitle>
                      <CardDescription className="mt-2 space-y-1">
                        <div className="flex items-center gap-4">
                          {docType && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{docType}</span>
                          )}
                          <span className="text-xs text-slate-500">{workflowName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>Current Step: <span className="font-semibold">{task.current_step}</span></span>
                          {task.assigned_roles && task.assigned_roles.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              <Users className="w-3 h-3" />
                              {task.assigned_roles.join(', ')}
                            </span>
                          )}
                        </div>
                        
                        {/* Due Date and live SLA countdown */}
                        {task.due_date && (
                          <div className={`flex items-center gap-2 text-xs ${overdue ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                            <Calendar className="w-3 h-3" />
                            <span>Due: {formatDate(task.due_date)}</span>
                            <SLABadge dueDate={task.due_date} nowMs={nowMs} />
                          </div>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <span
                        className={`px-3 py-1 rounded-full text-sm ${
                          task.status === 'In Progress'
                            ? 'bg-blue-100 text-blue-700'
                            : task.status === 'Changes Requested'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {task.status}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {task.history && task.history.length > 0 && (
                      <div className="bg-slate-50 p-3 rounded">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Recent History:</p>
                        <div className="space-y-1">
                          {task.history.slice(-3).map((entry, idx) => (
                            <div key={idx} className="text-xs text-slate-600">
                              <span className="font-medium">{entry.step}:</span> {entry.action}
                              {entry.comments && <span className="italic ml-1">- &ldquo;{entry.comments}&rdquo;</span>}
                              {entry.timestamp && <span className="text-slate-400 ml-2">({formatDate(entry.timestamp)})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-3 border-t items-center">
                      {task.doc_id && (
                        <a
                          href={`/documents/${task.doc_id}`}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline mr-auto"
                          data-testid={`preview-doc-${task.id}`}
                        >
                          <FileText className="w-4 h-4" />
                          View Document
                        </a>
                      )}
                      <Button
                        onClick={() => openCommentDialog(task.id, 'approve')}
                        data-testid={`approve-${task.id}`}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => openCommentDialog(task.id, 'reject')}
                        data-testid={`reject-${task.id}`}
                        size="sm"
                        variant="destructive"
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => openCommentDialog(task.id, 'request_changes')}
                        data-testid={`request-changes-${task.id}`}
                        size="sm"
                        variant="outline"
                      >
                        Request Changes
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            })
          )}
        </TabsContent>

        <TabsContent value="workflows" className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                onClick={() => setDefDialog({ open: true, workflow: null })}
                data-testid="create-workflow-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Workflow
              </Button>
            </div>
          )}
          {workflows.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No workflows defined</h3>
                <p className="text-slate-500">Contact your admin to create workflows</p>
              </CardContent>
            </Card>
          ) : (
            workflows.map((workflow) => (
              <Card key={workflow.id} data-testid={`workflow-${workflow.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{workflow.name}</CardTitle>
                      <CardDescription className="mt-1">
                        Status: {workflow.is_active ? 'Active' : 'Inactive'}
                      </CardDescription>
                      {workflow.sla_hours && (
                        <div className="flex items-center gap-1 text-sm text-slate-600 mt-2">
                          <Clock className="w-4 h-4" />
                          <span>SLA: {workflow.sla_hours} hours</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-sm ${
                          workflow.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {workflow.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDefDialog({ open: true, workflow })}
                          data-testid={`edit-workflow-${workflow.id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {workflow.json_definition?.steps && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-3">Workflow Steps:</p>
                        <div className="space-y-2">
                          {workflow.json_definition.steps.map((step, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900">{step.name}</span>
                                  {step.parallel && (
                                    <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                      <Users className="w-3 h-3" />
                                      Parallel Approval
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  {step.approvers?.length || 0} approver(s) required
                                  {step.parallel && ' (can approve independently)'}
                                </div>
                              </div>
                              {idx < workflow.json_definition.steps.length - 1 && (
                                <div className="flex-shrink-0 text-slate-300">↓</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {workflow.description && (
                      <div className="pt-3 border-t">
                        <p className="text-sm text-slate-600">{workflow.description}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* AI Workflow Builder Tab */}
        <TabsContent value="ai-builder" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                AI Workflow Builder
              </CardTitle>
              <CardDescription>
                Generate workflows from natural language descriptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <WorkflowBuilder 
                headers={getAuthHeaders()} 
                onWorkflowCreated={(workflow) => {
                  toast.success('Workflow created! Refresh to see it in the list.');
                  loadData();
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Chat Workflow Builder Tab */}
        <TabsContent value="ai-chat" className="space-y-4">
          <ConversationalBuilder 
            headers={getAuthHeaders()} 
            onWorkflowComplete={(workflow) => {
              toast.success('Workflow created through conversation!');
              loadData();
            }}
          />
        </TabsContent>

        {/* Document Lifecycle Tab */}
        <TabsContent value="lifecycle" className="space-y-4">
          <LifecycleDashboard headers={getAuthHeaders()} daysAhead={90} />
        </TabsContent>
      </Tabs>

      {/* Comment Dialog for Reject / Request Changes */}
      <Dialog open={commentDialog.open} onOpenChange={(o) => !o && closeCommentDialog()}>
        <DialogContent data-testid="workflow-comment-dialog">
          <DialogHeader>
            <DialogTitle>
              {commentDialog.action === 'reject'
                ? 'Reject Document'
                : commentDialog.action === 'approve'
                ? 'Approve with Note'
                : 'Request Changes'}
            </DialogTitle>
            <DialogDescription>
              {commentDialog.action === 'reject'
                ? 'Provide a clear reason for rejection. This will be recorded in the audit trail and shown to the requester.'
                : commentDialog.action === 'approve'
                ? 'Optionally attach an audit note to this approval. Useful for full-audit teams; leave blank to skip.'
                : 'Describe what changes are required so the requester can revise the document.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              data-testid="workflow-comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={
                commentDialog.action === 'reject'
                  ? 'e.g., Numbers in section 3 don\u2019t match the source ledger.'
                  : 'e.g., Please update the SLA table and re-attach the signed addendum.'
              }
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-slate-500 mt-2">
              {commentText.trim().length}/500 characters
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeCommentDialog}
              disabled={submitting}
              data-testid="workflow-comment-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitCommentAction}
              disabled={submitting || (commentDialog.action !== 'approve' && !commentText.trim())}
              data-testid="workflow-comment-submit"
              className={commentDialog.action === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' : commentDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
            >
              {submitting
                ? 'Submitting...'
                : commentDialog.action === 'reject'
                ? 'Confirm Rejection'
                : commentDialog.action === 'approve'
                ? 'Approve with Note'
                : 'Send Back for Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Workflow Definition Dialog */}
      <WorkflowDefinitionDialog
        open={defDialog.open}
        onOpenChange={(o) => setDefDialog((prev) => ({ ...prev, open: o }))}
        workflow={defDialog.workflow}
        headers={getAuthHeaders()}
        onSaved={loadData}
      />
    </div>
  );
};

export default Workflows;